import React, { useEffect, useContext, useCallback } from "react";
import useMap from "../../useMap";
import ContextMenuInteractionReadOnly, { extractMapEventFeature } from "./ContextMenu/MapContextMenuReadOnly";
import { getUtil } from "../../../../utils/positionable";
import { Context as InteractionContext } from "../InteractionContext";
import { Context as SelectionContext } from "../Select/SelectFeature";
import DataSourceContext from "../../DataSourceContext";
import { MapEditorRouteContext } from "../../../CustomRoutes/MapEditor";
import { MAP_VERSION_REMOVE_LOCKED_MESSAGE } from "../../mapVersionLock";


function FeatureDetails({ readOnly = false }) {
  const map = useMap();
  const interactionContext = useContext(InteractionContext);

  const selectionContext = useContext(SelectionContext);
  const dataSourceContext = useContext(DataSourceContext);
  const {
    setMode,
    setPositionableSourceModel,
    positionableSourceModel
  } = useContext(InteractionContext);
  const mapEditorRouteContext = useContext(MapEditorRouteContext);

  /**
   * SW2-2346 — edits are allowed on the newest map version only.
   *
   * The inspection-based test inside the callback fails open on a project with no
   * inspections: selectedInspectionId is undefined, so allowEditDueToSpecialInspection
   * is true and nothing is ever disabled. On project 5193 that let a legend item be
   * edited on v1 of 01-21-2025, which silently created a version dated today.
   *
   * Read at component scope so the callback below can list it as a dependency. Read
   * inside the callback it was invisible to the dependency array, which lists only
   * dataSourceContext and selectionContext — switching the picker changes neither, so
   * the menu kept answering with the version that was open when it was built.
   */
  const onOlderMapVersion = mapEditorRouteContext.isLatestMapVersion === false;

  // SW2-2541 — this menu is legend items only, so the lock applies to every remove it offers.
  const legendLocked = !!mapEditorRouteContext.legendLockedOnVersion;

  const getProjectUploadOptions = useCallback(
    (event, contextmenuFeature) => {
      const geometryType = contextmenuFeature && contextmenuFeature.get("geometry").getType();
      const selectedFeature = selectionContext.getSelectedFeature();
      const isRecentInsp = 
        mapEditorRouteContext.inspections?.length > 0 && 
        mapEditorRouteContext.inspections[0].id === mapEditorRouteContext.selectedInspection?.id;
      const selectedInspectionId = mapEditorRouteContext.selectedInspection?.id;
      const isInitialInspection = mapEditorRouteContext.inspections?.length === 1;
      const allowEditDueToSpecialInspection = !(selectedInspectionId) || isInitialInspection;

      // Check if feature is newly added (unsaved) - these don't have positionables_id yet
      const isNewFeature = selectedFeature && !selectedFeature.get('positionables_id');

      return (contextmenuFeature && contextmenuFeature.values_ && (contextmenuFeature.values_.positionable_type === "control_measure" || contextmenuFeature.values_.positionable_type === "pollutant"))
        ? (selectedFeature && selectedFeature.values_.is_deleted !== undefined && selectedFeature.values_.is_deleted === true)? [] : [
            {
              action: "modify",
              label: geometryType === "Point" ? "Move" : "Edit",
              onClick: () => {
                const feature = selectionContext.getSelectedFeature();

                if (feature) {
                  setPositionableSourceModel(null);
                  setMode("modify", { feature });
                }
              },
              isDisabled: (selectedFeature?.values_?.disable_options || (onOlderMapVersion && !isNewFeature) || (!isRecentInsp && !isNewFeature && !allowEditDueToSpecialInspection))? true : false,
              disabledMessage: (selectedFeature?.values_?.disable_options)? `${geometryType === "Point" ? "Move" : "Edit"} option is disabled because this legend item is deleted in future inspections !` : ((onOlderMapVersion && !isNewFeature) || (!isRecentInsp && !isNewFeature && !allowEditDueToSpecialInspection)) ? `Legend item cannot be ${geometryType === "Point" ? "moved" : "edited"} in previous versions of map!` : null
            },
            {
              action: "delete",
              label: "Remove From Map",
              onClick: event => {
                let feature = selectionContext.getSelectedFeature();

                if (!feature && event.feature) {
                  feature = event.feature;
                }

                if (feature) {
                  setMode("delete", {
                    feature,
                  });
                }
              },
              isDisabled: (selectedFeature?.values_?.disable_options ?? false) || legendLocked,
              disabledMessage: (selectedFeature?.values_?.disable_options)? `Remove From Map option is disabled because this legend item is deleted in future inspections !` : legendLocked ? MAP_VERSION_REMOVE_LOCKED_MESSAGE : null
            },
          ] : []
    },
    [dataSourceContext, selectionContext, mapEditorRouteContext, onOlderMapVersion, legendLocked],
  );

  const handleClick = useCallback(
    event => {
      const feature = extractMapEventFeature(event, map);

      let util;

      if (feature) {
        util = getUtil(feature.get("positionable_type"));
      }

      /**
       * Show overlay only if clicked on a feature
       * and overlay is supported by feature.
       *
       * If no feature clicked, close overlay, if active
       */
      if (feature && util && util.hasOverlay()) {
        interactionContext.setMode("detail", {
          feature,
          readOnly,
        });
      } else {
        interactionContext.setDefaultMode();
      }
    },
    [interactionContext],
  );

  useEffect(() => {
    map.on("singleclick", handleClick);

    return () => {
      map.un("singleclick", handleClick);
    };
  }, []);

  if(readOnly){
    return <ContextMenuInteractionReadOnly getProjectUploadOptions={getProjectUploadOptions} readOnly={readOnly} positionableSourceModel = {positionableSourceModel} />;
  }

  return <div />;
}

export default FeatureDetails;
